import type { SessionEvent, SessionMeta } from "./types"
import { postJson } from "./compress"

export interface TransportTargets {
  ingestUrl: string
  finalizeUrl: string
  replayUrl: string
}

export class Transport {
  constructor(
    private targets: TransportTargets,
    private meta: SessionMeta,
  ) {}

  updateMeta(partial: Partial<SessionMeta>): void {
    Object.assign(this.meta, partial)
  }

  async sendJourney(events: SessionEvent[]): Promise<void> {
    await postJson(this.targets.ingestUrl, this.journeyBody(events))
  }

  /**
   * The last batch of a page that is going away (pagehide / tab hidden). A normal fetch is cancelled
   * when the page unloads, and gzip is async, so it never even started: every page's final events —
   * its web vitals included — were lost (live: 1 of 20 page loads arrived). This sends synchronously,
   * uncompressed, with `keepalive`, in chunks under the browser's 64 KB keepalive budget; if the batch
   * is bigger than that, vitals / errors / navigation are kept first (order preserved).
   */
  sendJourneyOnUnload(events: SessionEvent[]): void {
    if (!events.length) return
    const BUDGET = 60_000
    const size = (evs: SessionEvent[]) => JSON.stringify(this.journeyBody(evs)).length
    let keep = events
    if (size(keep) > BUDGET) {
      const important = new Set(["vital", "error", "pageload", "page_view", "page_leave", "network_error", "form_submit"])
      keep = events.filter((e) => important.has(e.type))
    }
    let used = 0
    let chunk: SessionEvent[] = []
    const send = (evs: SessionEvent[]) => {
      const body = JSON.stringify(this.journeyBody(evs))
      used += body.length
      try {
        void fetch(this.targets.ingestUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => undefined)
      } catch {
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
          navigator.sendBeacon(this.targets.ingestUrl, new Blob([body], { type: "application/json" }))
        }
      }
    }
    // chunks of <= 30 KB (one oversized request must not starve the rest), until the budget is spent
    for (const e of keep) {
      if (chunk.length && size([...chunk, e]) > 30_000) {
        send(chunk)
        chunk = []
      }
      if (used + size([...chunk, e]) > BUDGET) break
      chunk.push(e)
    }
    if (chunk.length) send(chunk)
  }

  private journeyBody(events: SessionEvent[]): Record<string, unknown> {
    return {
      session_id: this.meta.session_id,
      visitor_id: this.meta.visitor_id,
      project_id: this.meta.project_id,
      ingest_key: this.meta.ingest_key || "",
      service_name: this.meta.service_name || "",
      started_at: this.meta.started_at,
      user_agent: this.meta.user_agent,
      device: this.meta.device,
      screen_size: this.meta.screen_size,
      user_id: this.meta.user_id || "",
      user_email: this.meta.user_email || "",
      user_traits: this.meta.user_traits || {},
      referrer: typeof document !== "undefined" ? document.referrer || "" : "",
      events,
    }
  }

  async sendReplay(events: unknown[], chunkIndex: number): Promise<void> {
    await postJson(this.targets.replayUrl, {
      session_id: this.meta.session_id,
      visitor_id: this.meta.visitor_id,
      project_id: this.meta.project_id,
      ingest_key: this.meta.ingest_key || "",
      chunk_index: chunkIndex,
      events,
    })
  }


  beaconFinalize(payload: Record<string, unknown>): boolean {
    const fullPayload = {
      ...payload,
      ingest_key: this.meta.ingest_key || "",
      service_name: this.meta.service_name || "",
      user_id: this.meta.user_id || "",
      user_email: this.meta.user_email || "",
      user_traits: this.meta.user_traits || {},
    }
    const body = new Blob([JSON.stringify(fullPayload)], { type: "application/json" })
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      return navigator.sendBeacon(this.targets.finalizeUrl, body)
    }
    void postJson(this.targets.finalizeUrl, fullPayload, true).catch(() => undefined)
    return true
  }
}
